import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="/AutomateSEO-logo.png" 
              alt="AutomateSEO.ai Logo" 
              className="w-11 h-11 rounded-xl shadow-lg"
            />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">About AutomateSEO.ai</h1>
        
        <div className="prose prose-lg max-w-none">
          <section className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500 mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Try AutomateSEO.ai Free!</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>3-Day Free Trial:</strong> Experience all features with no credit card required. Explore AI-powered SEO automation, content generation, and social media management completely free for 3 days.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Flexible Refund Policy:</strong> Not satisfied? Get a full refund within 3 days of your first payment. No questions asked.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Our Mission</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>AutomateSEO.ai is India's 1st fully automated AI platform for SEO, AEO, GEO & Social Media Automation.</strong>
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              We're on a mission to democratize enterprise-level digital marketing automation for businesses of all sizes. We believe that powerful AI-driven SEO, social media automation, and content generation should be accessible to everyone - not just large corporations with massive marketing budgets.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Founded in India with a vision to serve 1,000+ businesses globally, we're building the most comprehensive AI marketing automation platform that saves businesses 40+ hours weekly while delivering 300% better results than traditional manual marketing.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Who We Serve</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We serve ambitious businesses and marketing agencies who understand that time is their most valuable asset. Our customers include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Local Businesses:</strong> Restaurants, salons, retail stores, and service providers dominating their local markets</li>
              <li><strong>E-Commerce Stores:</strong> Online retailers scaling their organic traffic and social media presence</li>
              <li><strong>Marketing Agencies:</strong> Agencies managing multiple client accounts and campaigns simultaneously</li>
              <li><strong>Professional Services:</strong> Lawyers, doctors, consultants, and B2B services attracting qualified leads</li>
              <li><strong>Real Estate Professionals:</strong> Agents and brokers generating consistent property inquiries</li>
              <li><strong>Startups & SaaS:</strong> Fast-growing companies scaling marketing without proportional cost increases</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Our Technology</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              AutomateSEO.ai leverages cutting-edge artificial intelligence including GPT-4, DALL-E 3, and RunwayML Gen-3 to automate complex marketing tasks that traditionally require expert teams:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>AI SEO Automation:</strong> Automated keyword research, content optimization, technical audits, and rank tracking</li>
              <li><strong>Local SEO Intelligence:</strong> Google My Business optimization, citation building, and review management</li>
              <li><strong>Social Media Automation:</strong> Multi-platform scheduling, content generation, and engagement analytics</li>
              <li><strong>AI Content Creation:</strong> Professional images, videos, and written content generated in seconds</li>
              <li><strong>Predictive Analytics:</strong> AI forecasts trends and optimizes campaigns for maximum ROI</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Our Impact</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Since launching, we've helped businesses achieve remarkable results:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>1,000+ businesses served across 15+ countries</li>
              <li>40+ hours saved weekly per business on average</li>
              <li>300% average organic traffic increase within 90 days</li>
              <li>₹50,000+ monthly savings on agency fees per client</li>
              <li>95%+ customer retention rate due to proven results</li>
              <li>24/7 automated optimization with zero manual intervention</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Made in India 🇮🇳</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We're proud to be an Indian technology company competing globally. Built by Set India Business Private Limited, AutomateSEO.ai represents Indian innovation in artificial intelligence and digital marketing automation.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our development team in India works around the clock to ensure our platform stays at the cutting edge of AI technology, delivering world-class marketing automation that rivals any global competitor.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Our Values</h2>
            <ul className="space-y-4">
              <li>
                <strong className="text-gray-900 text-lg">Accessibility:</strong>
                <p className="text-gray-700">Enterprise-level automation should be affordable for businesses of all sizes</p>
              </li>
              <li>
                <strong className="text-gray-900 text-lg">Transparency:</strong>
                <p className="text-gray-700">Clear pricing, honest results, and no hidden fees or surprises</p>
              </li>
              <li>
                <strong className="text-gray-900 text-lg">Innovation:</strong>
                <p className="text-gray-700">Constantly improving our AI to deliver better results for our customers</p>
              </li>
              <li>
                <strong className="text-gray-900 text-lg">Results-Driven:</strong>
                <p className="text-gray-700">We measure success by customer outcomes, not vanity metrics</p>
              </li>
              <li>
                <strong className="text-gray-900 text-lg">Support:</strong>
                <p className="text-gray-700">24/7 customer support because your success is our success</p>
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Join Our Journey</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Whether you're a small business owner tired of spending hours on marketing, an agency looking to scale without hiring, or an entrepreneur building the next big thing - AutomateSEO.ai is your AI-powered marketing department.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Start your free trial today and experience the future of digital marketing automation. No credit card required, no technical expertise needed, and results guaranteed within 90 days.
            </p>
          </section>

          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Contact Us</h2>
            <p className="text-gray-700 mb-2"><strong>Email:</strong> support@automateseo.ai</p>
            <p className="text-gray-700 mb-2"><strong>Phone:</strong> +91 9810535398 (24/7 Support)</p>
            <p className="text-gray-700 mb-2"><strong>Company:</strong> Set India Business Private Limited</p>
            <p className="text-gray-700"><strong>Location:</strong> India</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;